import React, { useEffect, useState } from "react";
import DialogPopup from "../../components/DialogPopup";
import { Box, Button, Divider, Grid, Tooltip, Typography } from "@material-ui/core";
import translate from "../../i18n/Translator";
import { Cfdi, Concepto } from "../../model/Cfdi";
import NumberFormat from "react-number-format";
import Gridable from "../../components/Gridable";
import { DecimalsValidator, GTEZeroValidator, LTEValidator } from "../../components/Validators";
import ValidatedInput, { InputRef } from "../../components/ValidatedInput";
import { MappingConceptRequest } from "../../model/TenantCfdi";
import { WarningSnackbar } from "../../components/Snackbars";
import { ErrorIcon } from "../../components/Icons";
import { CheckCircle } from "@material-ui/icons";
import { green } from "@material-ui/core/colors";
import { color } from "@uiw/react-codemirror";

interface MapInvoiceConceptsWithCreditNotePopupProps {
    onSave(creditNoteId: string, request: MappingConceptRequest[]): any,
    onClose(creditNoteId: string): any,
    cfdiRelated: Cfdi,
    creditNote: Cfdi,
    creditNoteUseMapped: string,
    isReadOnly: boolean
}

interface MappingRequestWithConcept {
    request: MappingConceptRequest,
    conceptMapped: Concepto,
    amount_to_cancel: number
}

export default function MapInvoiceConceptsWithCreditNotePopup(props : MapInvoiceConceptsWithCreditNotePopupProps) {
    const [warning, setWarning] = useState<string>();
    const [validations, setValidations] = useState<any>({});
    const getCreditNoteAssociated = () => {
        return props.cfdiRelated.metadata.credit_notes?.find(c => c.credit_note_id === props.creditNote.id);
    }
    const buildConcepts = () => {
        let creditNoteAssociated = getCreditNoteAssociated();
        let conceptsRequest = [];
        let conceptos = props.cfdiRelated.cfdi!.conceptos;
        for(let i=0; i < conceptos.length; i ++) {
            let concepto = conceptos[i];
            if(creditNoteAssociated){
                let mappingConcept = creditNoteAssociated.mapping_concepts.find(c => c.concept_index == i);
                conceptsRequest[i] = {
                    conceptMapped: concepto, 
                    request: {
                        concept_index: i,
                        units_cancelled: mappingConcept ? mappingConcept.units_cancelled : 0
                    },
                    amount_to_cancel: concepto.valor_unitario * (mappingConcept ? mappingConcept.units_cancelled : 0), 
                } as MappingRequestWithConcept;
            } else {
                conceptsRequest[i] = {
                    conceptMapped: concepto, 
                    amount_to_cancel: 0,
                    request: {
                        concept_index: i,
                        units_cancelled: 0
                    }
                } as MappingRequestWithConcept;
            }
        }
        return conceptsRequest;
    }
    const [workingConcepts, setWorkingConcepts] = useState<MappingRequestWithConcept[]>(buildConcepts);

    const getMaxQuantityByConceptIdentifier = (concepto : Concepto, index: number ) : number => {
        return concepto.cantidad - (props.cfdiRelated.metadata.payment_info?.credit_notes.flatMap(credit_note => credit_note.mapping_concepts || []).filter(mapping_concept => index === mapping_concept.concept_index).reduce((total, concept) => total + concept.units_cancelled, 0) || 0);
    }

    const [totalSelectedToCancel, setTotalSelectedToCancel] = useState<number>(workingConcepts.map(workingConcept => workingConcept.amount_to_cancel).reduce((total, amount_to_cancel) => total + amount_to_cancel, 0));
    const [totalPendingAssigment, setTotalPendingAssigment] = useState<number>(props.creditNote.metadata.subtotal_base - totalSelectedToCancel);

    const onChangeQuantity = (name: string, value: any, inputRef:InputRef) => {
        let temp = workingConcepts ? workingConcepts : [];
        if("" == value) {
            value = 0;
        }
        temp.forEach((workingConcept) => {
            if(name === `${workingConcept.request.concept_index}`) {
                workingConcept.request.units_cancelled = value;
                workingConcept.amount_to_cancel = workingConcept.conceptMapped.valor_unitario * value;
            }
        });

        validations[name] = inputRef;
        setValidations(validations);

        setWorkingConcepts(temp);
        let tempTotalSelectedToCancel = temp.map(workingConcept => workingConcept.amount_to_cancel).reduce((total, amount_to_cancel) => total + amount_to_cancel, 0);
        setTotalSelectedToCancel(tempTotalSelectedToCancel)
        setTotalPendingAssigment(props.creditNote.metadata.subtotal_base - tempTotalSelectedToCancel);
    }

    const validateAmountPendingAssigmentOutOfRange = (amount : number) : boolean => {
        const tolerance = 0.01;
        return (amount < -tolerance || amount > tolerance);
    }

    const onSave = () => {
        if (!isValid()) return;

        if(validateAmountPendingAssigmentOutOfRange(totalPendingAssigment)) {
            setWarning(translate("cfdis.credit_notes.map_concepts_popup.error.pending_assignment_out_of_range") as string);
            return;
        }
        props.onSave(props.creditNote.id, workingConcepts.map(w => w.request));
    }

    const isValid = () => {
        var valid = true;
        for (let field in validations) {
            let ref = validations[field];
            if (!ref.valid) {
                ref.blurer(true);
                valid = false;
            }
        }
        return valid;
    }

    const onClosedSnackbar = () => {
        setWarning(undefined);
    };

    const getInvoiceBalanceAfterCreditNote = () => {
        let creditNoteAssociated = getCreditNoteAssociated();
        return creditNoteAssociated ? creditNoteAssociated.new_balance : 0;
    }

    return (
        <DialogPopup title={props.isReadOnly ? translate("cfdis.credit_notes.map_concepts_popup.title_read_only") as string : translate("cfdis.credit_notes.map_concepts_popup.title") as string} open onClose={() => {props.onClose(props.creditNote.id)}} maxWidth="lg" button={            
            props.isReadOnly ? <></>: <Button onClick={() => {onSave()}
                }
                variant="contained" color="primary" size="medium" disabled={false}>
                {translate("buttons.accept")}
            </Button>} disableBackdropClick={true} disableEscapeKeyDown={true} variant={props.isReadOnly ? "contained" : "outlined"}>
            <CfdiInfoSection 
                subtitle={translate("cfdis.credit_notes.map_concepts_popup.credit_note_subtitle") as string} subtitle_identifier={translate("cfdis.credit_notes.map_concepts_popup.credit_note") as string} identifier={props.creditNote.identifier? props.creditNote.identifier : props.creditNote.cfdi!.timbre.uuid.substring(-5, 5)} description_subtitle={translate("cfdis.credit_notes.map_concepts_popup.credit_note_use") as string} description={translate(`cfdis.credit_notes.select_use_popup.uses.${props.creditNoteUseMapped}`) as string} subtotal_subtitle={translate("cfdis.credit_notes.map_concepts_popup.credit_note_subtotal") as string} subtotal={props.creditNote.cfdi!.subtotal} currency={props.creditNote.currency} />
            <br/>
            <CfdiInfoSection
                subtitle={translate("cfdis.credit_notes.map_concepts_popup.invoice_subtitle") as string} subtitle_identifier={translate("cfdis.credit_notes.map_concepts_popup.invoice") as string} identifier={props.cfdiRelated.identifier ? props.cfdiRelated.identifier : props.cfdiRelated.cfdi!.timbre.uuid.substring(-5, 5)} description_subtitle={translate("cfdis.credit_notes.map_concepts_popup.invoice_description") as string} description={props.cfdiRelated.description} subtotal_subtitle={translate("cfdis.credit_notes.map_concepts_popup.invoice_subtotal") as string} subtotal={props.cfdiRelated.cfdi!.subtotal} currency={props.cfdiRelated.currency}/>
            <br/>
            <CfdiConceptsSection workingConcepts={workingConcepts} isReadOnly={props.isReadOnly} onChangeQuantity={onChangeQuantity} getMaxQuantityByConceptIdentifier={getMaxQuantityByConceptIdentifier}
            />
            <Divider /> <br/>
            <AmountsResume cfdiRelated={props.cfdiRelated} creditNote={props.creditNote} isReadOnly={props.isReadOnly} totalPendingAssigment={totalPendingAssigment} totalSelectedToCancel={totalSelectedToCancel} validateAmountPendingAssigmentOutOfRange={validateAmountPendingAssigmentOutOfRange} getInvoiceBalanceAfterCreditNote={getInvoiceBalanceAfterCreditNote} /> 
            <WarningSnackbar message={warning} onClose={onClosedSnackbar} />
        </DialogPopup>
    );
}

interface QuantityFieldProps {
    itemCode: string;
    maxQuantity: number;
    quantity?: number;
    onChangeQuantity(name: string, value: number | string, inputRef: InputRef): any;
}

function QuantityField(props: QuantityFieldProps) {
    const gtZeroValidator = new GTEZeroValidator();
    const ltValidator = new LTEValidator(props.maxQuantity);
    const decimalValidator = new DecimalsValidator(6);
    return <div>
        <Grid container >
            <Grid item xs={10} sm={10} md={10} lg={10} >
                <ValidatedInput
                    type="number"
                    id={props.itemCode}
                    name={props.itemCode}
                    value={props.quantity?.toString() || ""}
                    validators={[gtZeroValidator, decimalValidator, ltValidator]}
                    label={""}
                    margin="dense"
                    disableDefaultValidator={true}
                    onValueChanged={props.onChangeQuantity}
                />
            </Grid>
            <Grid item xs={2} sm={2} md={2} lg={2} >
                <Box pt={2} pl={1}>
                    {"/" + props.maxQuantity}
                </Box>
            </Grid>
        </Grid>
    </div>;
}

interface CfdiInfoSection {
    subtitle: string, 
    subtitle_identifier: string, 
    identifier: string, 
    description_subtitle: string, 
    description: string, 
    subtotal_subtitle: string, 
    subtotal: number, 
    currency: string   
} 

function CfdiInfoSection(props: CfdiInfoSection) {

    return (
        <Grid container>
          <Grid item xs={12}>
            <Typography color="primary">{props.subtitle}</Typography>
            <Box px={2} py={2} style={{ borderStyle: "solid", borderRadius: 20, borderColor: "#DFDCDC", }}>
              <Grid container>
                <Grid item xs={3}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography style={{ color: "#686868" }}>{props.subtitle_identifier} </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>{props.identifier}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={3}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography style={{ color: "#686868" }}>{props.description_subtitle} </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography> {props.description} </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end" >
                        <Grid item>
                          <Typography style={{ color: "#686868" }}> {props.subtotal_subtitle} </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end" >
                        <Grid item>
                            <Typography><NumberFormat value={props.subtotal} prefix="$ " decimalScale={2} fixedDecimalScale={true} thousandSeparator="," displayType="text" suffix={props.currency}/></Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      );
}

interface CfdiConceptsSectionProps {
    workingConcepts: MappingRequestWithConcept[],
    isReadOnly: boolean,
    onChangeQuantity(name: string, value: number, inputRef: InputRef): any,
    getMaxQuantityByConceptIdentifier(concepto : Concepto, index: number): any
}

function CfdiConceptsSection(props : CfdiConceptsSectionProps) {

    return (
        <Grid container>
                    <Grid item xs={12}>
                        <Box mx={-3}>
                            <Gridable items={props.workingConcepts} loading={false} empty="" columns={[
                                {
                                    title: translate("cfdis.credit_notes.map_concepts_popup.concepts.unit") as string,
                                    converter: (concepto) => concepto.conceptMapped.unidad || "",
                                    xs: true,
                                    sm: true,
                                    md: true,
                                    xl: true
                                },
                                {
                                    title: translate("cfdis.credit_notes.map_concepts_popup.concepts.concept") as string,
                                    converter: (concepto) => concepto.conceptMapped.description || "",
                                    xs: true,
                                    sm: true,
                                    md: true,
                                    xl: true
                                },
                                {
                                    title: translate("cfdis.credit_notes.map_concepts_popup.concepts.quantity") as string,
                                    converter: (concepto) => concepto.conceptMapped.cantidad || "",
                                    xs: true,
                                    sm: true,
                                    md: true,
                                    xl: true
                                },
                                {
                                    title: translate(`cfdis.credit_notes.map_concepts_popup.concepts.${props.isReadOnly ? "quantity_cancelled" : "quantity_to_cancel"}`) as string,
                                    converter: (concepto) => (
                                        props.isReadOnly ? <NumberFormat value={concepto.request.units_cancelled} prefix="" decimalScale={6} fixedDecimalScale={true} displayType="text" /> :
                                        <QuantityField itemCode={`${concepto.request.concept_index}`} quantity={concepto.request.units_cancelled} maxQuantity={props.getMaxQuantityByConceptIdentifier(concepto.conceptMapped, concepto.request.concept_index)} onChangeQuantity={props.onChangeQuantity}/>
                                    ),
                                    fullWidth: true,
                                    xs: 2,
                                    sm: 2,
                                    md: 3,
                                    xl: 3
                                },
                                {
                                    title: translate("cfdis.credit_notes.map_concepts_popup.concepts.unit_price") as string,
                                    converter: (concepto) => (
                                        <NumberFormat value={concepto.conceptMapped.valor_unitario} prefix="$ " decimalScale={2} fixedDecimalScale={true} displayType="text" />
                                    ),
                                    xs: 2,
                                    sm: 2,
                                    md: 2,
                                    xl: 2
                                },
                                {
                                    title: translate("cfdis.credit_notes.map_concepts_popup.concepts.amount") as string,
                                    converter: (concepto) => (
                                        <NumberFormat value={concepto.conceptMapped.importe} prefix="$ " decimalScale={2} fixedDecimalScale={true} displayType="text" />
                                    ),
                                    xs: 2,
                                    sm: 2,
                                    md: 2,
                                    xl: 2
                                },
                                {
                                    title: translate("cfdis.credit_notes.map_concepts_popup.concepts.amount_to_cancel") as string,
                                    converter: (concepto) => (
                                        <Typography color="secondary">
                                            <NumberFormat value={concepto.conceptMapped.valor_unitario * concepto.request.units_cancelled
                                        } prefix="$ " decimalScale={2} fixedDecimalScale={true} displayType="text" />
                                        </Typography>
                                        
                                    ),
                                    xs: 2,
                                    sm: 2,
                                    md: 2,
                                    xl: 2
                                }
                            ]}
                            />
                        </Box>
                    </Grid>
                </Grid>
    );
}

interface AmountsResumeProps {
    cfdiRelated: Cfdi,
    creditNote: Cfdi,
    totalSelectedToCancel: number,
    isReadOnly: boolean,
    totalPendingAssigment: number,
    getInvoiceBalanceAfterCreditNote(): any,
    validateAmountPendingAssigmentOutOfRange(amount: number): any
}   

function AmountsResume(props : AmountsResumeProps){

    return (
        <Grid container>
                    <Grid item xs={12}>
                        <Box px={2} py={2}>
                            <Grid container spacing={4} >
                            {!props.isReadOnly && ( <>
                                <Grid item xs={true}>
                                    <Grid container>
                                    <Grid item xs={12}>
                                        <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end" >
                                            <Grid item>
                                                {translate("cfdis.credit_notes.map_concepts_popup.invoice_subtotal")}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end" >
                                            <Grid item>
                                                <Typography><NumberFormat value={props.cfdiRelated.metadata.subtotal_base}
                                                    prefix="$ " decimalScale={2} fixedDecimalScale={true}
                                                    thousandSeparator="," displayType="text" suffix={""}
                                                /></Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    </Grid>
                                </Grid>
                                <Divider orientation="vertical" flexItem />
                                <Grid item xs={true}>
                                    <Grid container >
                                        <Grid item xs={12}>
                                            <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end" >
                                                <Grid item style={{ color:"#686868" }}>
                                                    {translate("cfdis.credit_notes.map_concepts_popup.subtotal_credit_note")}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end" >
                                                <Grid item>
                                                    <Typography color="primary"><NumberFormat value={props.creditNote.metadata.subtotal_base}
                                                        prefix="$ " decimalScale={2} fixedDecimalScale={true}
                                                        thousandSeparator="," displayType="text" suffix={""}
                                                    /></Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Divider orientation="vertical" flexItem />
                                <Grid item xs={true}>
                                    <Grid container >
                                        <Grid item xs={12}>
                                            <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end" >
                                                <Grid item style={{ color:"#686868" }}>
                                                    {translate("cfdis.credit_notes.map_concepts_popup.selected_to_cancel")}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end" >
                                                <Grid item>
                                                    <Typography color="secondary"><NumberFormat value={props.totalSelectedToCancel}
                                                        prefix="$ " decimalScale={2} fixedDecimalScale={true}
                                                        thousandSeparator="," displayType="text" suffix={""}
                                                    /></Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Divider orientation="vertical" flexItem />
                                    <Grid item xs={true}>
                                        <Grid container >
                                            <Grid item xs={12}>
                                                <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end" >
                                                    <Grid item style={{ color:"#686868" }}>
                                                        {translate("cfdis.credit_notes.map_concepts_popup.pending_assignment")}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end" >
                                                    <Grid item>
                                                        {props.validateAmountPendingAssigmentOutOfRange(props.totalPendingAssigment) ? <Tooltip title={translate("cfdis.credit_notes.map_concepts_popup.error.tooltip") as string}><ErrorIcon color="secondary"/></Tooltip> : <CheckCircle htmlColor="green"/>}
                                                    </Grid>
                                                    <Grid item >
                                                        <Typography style={{fontWeight: "bolder"}}><NumberFormat value={props.totalPendingAssigment}
                                                            prefix="$ " decimalScale={2} fixedDecimalScale={true}
                                                            thousandSeparator="," displayType="text" suffix={""}
                                                        /></Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                            </>)}
                                {props.isReadOnly && (
                                    <Grid item xs={true}>
                                        <Grid container >
                                            <Grid item xs={12}>
                                                <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end" >
                                                    <Grid item style={{ color:"#686868" }}>
                                                        {translate("cfdis.credit_notes.map_concepts_popup.balance")}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Grid container alignContent="flex-end" alignItems="flex-end" justifyContent="flex-end" >
                                                    <Grid item>
                                                        <Typography style={{fontWeight: "bolder"}}><NumberFormat value={props.getInvoiceBalanceAfterCreditNote()}
                                                            prefix="$ " decimalScale={2} fixedDecimalScale={true}
                                                            thousandSeparator="," displayType="text" suffix={""}
                                                        /></Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
    );
}